// src/pages/PftExtractionPanel.js
//
// The admin upload page's file list when PFT extraction is on (flag
// PFT_EXTRACTION_ENABLED): every uploaded PDF in one of four sections, and the
// "Extract next batch" button. The server picks the files (oldest upload first, never
// one already extracted); the extraction runs in the cloud, so the page can be closed.
// Costs are recorded on each run (pft_runs.costUsd) but deliberately not shown here.

import { useEffect, useMemo, useState } from "react";
import { RefreshCw, Play, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { displayName } from "../services/pftUploadPlan";
import {
  BATCH_SIZES,
  isRunActive,
  latestRun,
  nextBatchCount,
  runProgress,
  sortIntoSections,
} from "../services/pftExtractionStatus";
import { startPftExtraction, watchPftFiles, watchPftRuns } from "../services/pftExtractionService";

const LIST_LIMIT = 300;
const SECTIONS = [
  { key: "notExtracted", label: "Not extracted yet" },
  { key: "extracting", label: "Extracting now" },
  { key: "extracted", label: "Extracted" },
  { key: "failed", label: "Failed" },
];
const btn =
  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[12px] text-slate-200 transition disabled:opacity-40";

const when = (iso) => {
  const t = Date.parse(iso || "");
  return Number.isNaN(t) ? "" : new Date(t).toLocaleString();
};

export default function PftExtractionPanel({ tenantId, siteId, folder, uploaded, uploadedError, loadingUploaded, onRefresh }) {
  const [files, setFiles] = useState([]);
  const [runs, setRuns] = useState([]);
  const [statusError, setStatusError] = useState(false);
  const [tab, setTab] = useState("notExtracted");
  const [filter, setFilter] = useState("");
  const [size, setSize] = useState(500);
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [message, setMessage] = useState(null); // { tone, text }
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const fail = () => setStatusError(true);
    const offFiles = watchPftFiles((list) => { setFiles(list); setStatusError(false); }, fail);
    const offRuns = watchPftRuns(setRuns, fail);
    const tick = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => {
      offFiles();
      offRuns();
      clearInterval(tick);
    };
  }, [tenantId, siteId]);

  const last = latestRun(runs);
  const active = isRunActive(last, now) ? last : null;
  const sections = useMemo(
    () => sortIntoSections(uploaded || [], `uploads/${tenantId}/${folder}/`, files, active && active.id),
    [uploaded, tenantId, folder, files, active],
  );
  const count = nextBatchCount(sections, size);
  const progress = runProgress(active);

  const start = async () => {
    setConfirming(false);
    setStarting(true);
    setMessage(null);
    try {
      const r = await startPftExtraction({ tenantId, siteId, count: size });
      setMessage(
        r.started
          ? { tone: "ok", text: `Extraction started on ${r.files} PDFs. It runs in the cloud: you can close this page.` }
          : { tone: "info", text: r.reason || "Nothing to extract." },
      );
      if (r.started) setTab("extracting");
    } catch (e) {
      setMessage({ tone: "warn", text: e.message });
    } finally {
      setStarting(false);
    }
  };

  const list = sections[tab] || [];
  const q = filter.trim().toLowerCase();
  const matches = list.filter((f) => !q || displayName(f.name).toLowerCase().includes(q));
  const extractedNoPft = sections.extracted.filter((f) => f.state && f.state.status !== "duplicate" && f.state.tests === 0).length;

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-5 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-semibold">Uploaded files{uploaded ? ` (${uploaded.length})` : ""}</h2>
        <button onClick={onRefresh} disabled={loadingUploaded} className={`ml-auto ${btn}`}>
          <RefreshCw className={`w-3.5 h-3.5 ${loadingUploaded ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* ---- extraction ---- */}
      <div className="rounded-xl border border-white/10 bg-white/[0.025] p-4 space-y-3">
        {active ? (
          <>
            <div className="flex items-center gap-2 text-sm text-slate-100">
              <Loader2 className="w-4 h-4 animate-spin" />
              Extracting {progress.total} PDFs: {progress.finished} of {progress.total} done
            </div>
            <div className="h-2.5 rounded-full border border-white/15 bg-white/[0.06] overflow-hidden" aria-label={`${progress.pct}% extracted`}>
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress.pct}%` }} />
            </div>
            <p className="text-[12px] text-slate-400">
              Started {when(active.createdAt)} by {active.startedBy}. It runs in the cloud: this page can be closed.
              {active.failed ? ` ${active.failed} failed so far (they'll be tried again next run).` : ""}
            </p>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-slate-200">Extract the next</span>
              <select
                value={size}
                onChange={(e) => setSize(Number(e.target.value))}
                disabled={starting}
                aria-label="How many PDFs to extract"
                className="px-2 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:outline-none"
              >
                {BATCH_SIZES.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <span className="text-slate-200">PDFs</span>
              <button
                onClick={() => setConfirming(true)}
                disabled={starting || count === 0 || statusError || !uploaded}
                className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500/40 to-blue-500/40 border border-purple-300/30 hover:from-purple-500/60 hover:to-blue-500/60 text-sm font-medium text-white transition disabled:opacity-40"
              >
                {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                Extract next {count}
              </button>
            </div>
            <p className="text-[12px] text-slate-400">
              {count === 0
                ? "Every uploaded PDF has been extracted."
                : `Takes the ${count} oldest uploads not yet extracted. PDFs already extracted are never read again.`}
            </p>
            {confirming && (
              <div className="rounded-lg border border-amber-300/30 bg-amber-500/10 p-3 text-[13px] text-amber-100 space-y-2">
                <p>
                  Extract {count} PDFs? It runs in the cloud and takes a while; you can close this page.
                </p>
                <div className="flex gap-2">
                  <button onClick={start} className={btn}>Yes, start</button>
                  <button onClick={() => setConfirming(false)} className={btn}>Cancel</button>
                </div>
              </div>
            )}
            {last && !active && runProgress(last).total > 0 && (
              <p className="text-[12px] text-slate-400">
                Last run ({when(last.createdAt)}): {last.done || 0} extracted
                {last.duplicates ? `, ${last.duplicates} identical cop${last.duplicates === 1 ? "y" : "ies"} skipped` : ""}
                {last.failed ? `, ${last.failed} failed` : ""}, {last.tests || 0} PFT tests found
                {last.flaggedTests ? ` (${last.flaggedTests} need a person to check)` : ""}
                {last.status && last.status !== "finished" ? ` · ${last.status}` : ""}.
              </p>
            )}
          </>
        )}
        {last && !active && last.status === "stopped" && last.stoppedReason && (
          <p className="text-[12px] text-amber-200 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            Last run stopped: {last.stoppedReason} Files it didn't finish are back under Not extracted yet.
          </p>
        )}
        {message && (
          <p className={`text-[12px] flex items-center gap-1.5 ${message.tone === "warn" ? "text-amber-200" : "text-emerald-200"}`}>
            {message.tone === "warn" ? <AlertTriangle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {message.text}
          </p>
        )}
        {statusError && (
          <p className="text-[12px] text-amber-200">Couldn't load the extraction status. Try reloading the page.</p>
        )}
      </div>

      {/* ---- sections ---- */}
      {uploadedError ? (
        <p className="text-[12px] text-amber-200">Couldn't load the list of uploaded files. Try Refresh.</p>
      ) : uploaded === null ? (
        <p className="text-[12px] text-slate-400">Loading…</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2" role="tablist">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                role="tab"
                aria-selected={tab === s.key}
                onClick={() => setTab(s.key)}
                className={`px-3 py-1.5 rounded-lg border text-[12px] transition ${
                  tab === s.key
                    ? "bg-purple-500/20 border-purple-300/40 text-white"
                    : "bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/[0.08]"
                }`}
              >
                {s.label} ({sections[s.key].length})
              </button>
            ))}
          </div>
          {tab === "extracted" && extractedNoPft > 0 && (
            <p className="text-[12px] text-amber-200">
              {extractedNoPft} extracted PDF{extractedNoPft === 1 ? "" : "s"} had no PFT report found in it. Check those files.
            </p>
          )}
          {list.length === 0 ? (
            <p className="text-[12px] text-slate-400">Nothing here.</p>
          ) : (
            <>
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search file names"
                aria-label="Search file names"
                className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-purple-400/50"
              />
              <ul className="max-h-72 overflow-auto rounded-xl border border-white/10 divide-y divide-white/5 text-[12px]">
                {matches.slice(0, LIST_LIMIT).map(({ name, state }) => (
                  <li key={name} className="px-3 py-1.5 flex flex-wrap items-center gap-2">
                    <span className="text-slate-200 break-all">{displayName(name)}</span>
                    {tab === "extracted" && state && state.status === "duplicate" && (
                      <span className="ml-auto text-[11px] text-slate-400">
                        Same file as {state.duplicateOfName || "another upload"} (not read again)
                      </span>
                    )}
                    {tab === "extracted" && state && state.status !== "duplicate" && (
                      <span className="ml-auto text-[11px] text-slate-400">
                        {state.tests === 0 ? "no PFT found" : `${state.tests} PFT test${state.tests === 1 ? "" : "s"}`}
                        {state.flaggedTests ? ` · ${state.flaggedTests} to check` : ""}
                      </span>
                    )}
                    {tab === "failed" && state && (
                      <span className="ml-auto text-[11px] text-amber-200">{state.error || "failed"}</span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-slate-400">
                {matches.length > LIST_LIMIT
                  ? `Showing ${LIST_LIMIT} of ${matches.length}. Search to narrow the list.`
                  : `${matches.length} file${matches.length === 1 ? "" : "s"}${q ? " match" : ""}.`}
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}
